import React, { useEffect, useState, useRef } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import {
  fetchUserNotificationsSuite,
  supportFetchUserNotificationsSuite,
  updateUserNotificationsSuite,
  supportUpdateUserNotificationsSuite
} from 'actions/Notifications';
import debounce from "lodash/debounce";
import Toggle from "components/Toggle";
import Tooltip from "components/Tooltip";
import CheckboxInput from "components/CheckboxInput";

const UserSettingsNotificationsModal = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const entryNotificationsRef = useRef(); 
  const gatewayOfflineRef = useRef(); 
  const lowBatteryRef = useRef(); 
  const manualNotificationsRef = useRef(); 
  const motionNotificationsRef = useRef(); 
  const otherNotificationsRef = useRef(); 
  const repoNotificationsRef = useRef(); 
  const targettedNotificationsRef = useRef(); 
  const unitNotificationsRef = useRef(); 
  const user = useSelector(state => state.users.userObject);
  const authUser = useSelector(state => state.auth.authUser);
  const notificationsSuite = useSelector((state) => state.notifications.notificationsSuite);
  const supportNotificationsSuite = useSelector((state) => state.notifications.supportNotificationsSuite);
  const site = useSelector(state => state.auth.site);
  const [notifications, setNotifications] = useState(null);

  const baseStyles = "px-4 py-3 tw-paragraph-sm";

  const notificationsMap = {
    entryNotifications: {
      label: intl.formatMessage({ id: "form.entryNotificationsEnabled" }),
      ref: entryNotificationsRef,
      tooltipText: intl.formatMessage({ id: "tooltip.notifications.user.entry" }),
    },
    gatewayOffline: {
      label: intl.formatMessage({ id: "form.gatewayOfflineNotificationsEnabled" }),
      ref: gatewayOfflineRef,
      tooltipText: intl.formatMessage({ id: "tooltip.notifications.user.gateway" }),
    },
    lowBattery: {
      label: intl.formatMessage({ id: "form.lowBatteryNotificationsEnabled" }),
      ref: lowBatteryRef,
      tooltipText: intl.formatMessage({ id: "tooltip.notifications.user.lowBattery" }),
    },
    manualNotifications: {
      label: intl.formatMessage({ id: "form.manualNotificationsEnabled" }),
      ref: manualNotificationsRef,
      tooltipText: intl.formatMessage({ id: "tooltip.notifications.user.manual" }),
    },
    motionNotifications: {
      label: intl.formatMessage({ id: "form.motionNotificationsEnabled" }),
      ref: motionNotificationsRef,
      tooltipText: intl.formatMessage({ id: "tooltip.notifications.user.motion" }),
    },
    otherNotifications: {
      label: intl.formatMessage({ id: "form.otherNotificationsEnabled" }),
      ref: otherNotificationsRef,
      tooltipText: intl.formatMessage({ id: "tooltip.notifications.user.other" }),
    },
    repoNotifications: {
      label: intl.formatMessage({ id: "form.repoNotificationsEnabled" }),
      ref: repoNotificationsRef,
      tooltipText: intl.formatMessage({ id: "tooltip.notifications.user.repo" }),
    },
    targettedNotifications: {
      label: intl.formatMessage({ id: "comp.table.header.targetedNotifications" }),
      ref: targettedNotificationsRef,
      tooltipText: intl.formatMessage({ id: "tooltip.notifications.user.targetted" }),
    },
    unitNotifications: {
      label: intl.formatMessage({ id: "form.unitNotificationsEnabled" }),
      ref: unitNotificationsRef,
      tooltipText: intl.formatMessage({ id: "tooltip.notifications.user.unit" }),
    },
  }

  useEffect(() => {
    if (!authUser || !user) return;

    authUser.id === user.id
    ? dispatch(fetchUserNotificationsSuite())
    : dispatch(supportFetchUserNotificationsSuite(user.uuid));

  }, [authUser, user]);

  useEffect(() => {
    // notifications are either set from notificationsSuite (user is editing their own notifications) or supportNotificationsSuite (user is editing another user's notifications)
    // temperature and unit offline are filtered out as they are not currently used
    if (notificationsSuite && authUser?.id === user?.id) {
      setNotifications(
        {
          ...notificationsSuite,
          userNotificationsSettings: notificationsSuite.userNotificationsSettings.filter(n => n.notificationsType !== "temperature" && n.notificationsType !== "unitOffline")
        }
      );
    }
    if (supportNotificationsSuite && authUser?.id !== user?.id) {
      setNotifications(
        {
          ...supportNotificationsSuite,
          userNotificationsSettings: supportNotificationsSuite.userNotificationsSettings.filter(n => n.notificationsType !== "temperature" && n.notificationsType !== "unitOffline")
        }
      );
    }
  }, [notificationsSuite, supportNotificationsSuite]);

  const saveNotifications = debounce((updatedNotifications) => {
    if (authUser?.id === user?.id) {
      dispatch(
        updateUserNotificationsSuite(
          updatedNotifications,
          {
            success: intl.formatMessage({ id: 'notifications.success.edit' }),
            error: intl.formatMessage({ id: 'notifications.error.failed' })
          }
        )
      );
    } else {
      dispatch(
        supportUpdateUserNotificationsSuite(
          updatedNotifications,
          {
            success: intl.formatMessage({ id: 'notifications.success.edit' }),
            error: intl.formatMessage({ id: 'notifications.error.failed' })
          }
        )
      );
    }
  }, 1500);

  // Toggle all notifications on/off
  const handleAllNotificationsToggle = () => {
    const updatedNotifications = {
      ...notifications,
      ...authUser?.id === user?.id ? {} : { userUUID: user.uuid },
      notificationsOn: !notifications.notificationsOn,
    };
    setNotifications(updatedNotifications);
    saveNotifications(updatedNotifications);
  }

  // toggle a whole column of notifications
  const handleSelectAll = (type, value) => {
    const updatedNotifications = {
      ...notifications,
      ...authUser?.id === user?.id ? {} : { userUUID: user.uuid },
      userNotificationsSettings: notifications.userNotificationsSettings.map((setting) => {
        if (setting.notificationsType === "targettedNotifications" && (type === "notificationsEmails" || type === "notificationsSMS")) {
          return setting;
        }
        return {
          ...setting,
          [type]: value, // Update only the specified type
        };
      }),
    };
    setNotifications(updatedNotifications);
    saveNotifications(updatedNotifications);
  }

  // toggle individual notification
  const handleToggleButton = (id, type) => {
    const updatedNotifications = {
      ...notifications,
      ...authUser?.id === user?.id ? {} : { userUUID: user.uuid },
      userNotificationsSettings: notifications.userNotificationsSettings.map((setting) =>
        setting.id === id
          ? {
              ...setting,
              [type]: !setting[type],
            }
          : setting
      ),
    };
    setNotifications(updatedNotifications);
    saveNotifications(updatedNotifications);
  }

  return (
    <div className="min-h-[300px] max-h-[50vh] px-10 pt-4 flex flex-col justify-between overflow-scroll custom-scrollbar">
      <div>
        <h5 className="tw-heading-5 mb-8">{intl.formatMessage({ id: "pages.notifications.Types" })}</h5>
      </div>
      {site?.notifications_on == "false"
        ? <div className="flex flex-col gap-2">
          <h5 className="tw-heading-5">
            {intl.formatMessage({ id: "notifications.disabled" })}
          </h5>
          <p className="tw-paragraph-lg">
          {intl.formatMessage({ id: "notifications.disabledBody" })}
          </p>
        </div>
        : <div className="flex flex-col gap-4">
          <p className="tw-paragraph-lg">
            {intl.formatMessage(
              {
                id: authUser?.id == user?.id
                  ? `form.globalnotifs.userNotificationsDesc`
                  : `form.globalnotifs.otherUserNotificationsDesc`
              }
            )}
          </p>
          {/* All User Notifications Toggle */}
          <div className="flex justify-between">
            <p className="tw-paragraph-reg">
              {intl.formatMessage({ id: "form.globalnotifs.allUserNotifs" })}
            </p>
            <Toggle
              size="sm"
              label=""
              checked={notifications?.notificationsOn}
              onChange={() => handleAllNotificationsToggle()}
            />
          </div>
          <div className="grid grid-cols-[60%_1fr_1fr_1fr] gap-y-4 items-center">
            {notifications?.notificationsOn && <React.Fragment>
              <p></p>
              <div className="flex justify-center items-center">
                <CheckboxInput
                  id="US-select-all-emails-notifications"
                  dataCy="US-select-all-emails-notifications"
                  hideLabel={true}
                  checked={notifications.userNotificationsSettings
                    .filter((setting) => setting.notificationsType !== "targettedNotifications")
                    .every((setting) => setting.notificationsEmails)
                  }
                  align="center"
                  onChange={(val) => handleSelectAll("notificationsEmails", val)}
                />
              </div>
              <div className="flex justify-center items-center">
                <CheckboxInput
                  id="US-select-all-push-notifications"
                  dataCy="US-select-all-push-notifications"
                  hideLabel={true}
                  checked={notifications.userNotificationsSettings.every((setting) => setting.notificationsPush)}
                  align="center"
                  onChange={(val) => handleSelectAll("notificationsPush", val)}
                />
              </div>
              <div className="flex justify-center items-center">
                <CheckboxInput
                  id="US-select-all-sms-notifications"
                  dataCy="US-select-all-sms-notifications"
                  hideLabel={true}
                  checked={notifications.userNotificationsSettings
                    .filter((setting) => setting.notificationsType !== "targettedNotifications")
                    .every((setting) => setting.notificationsSMS)
                  }
                  align="center"
                  onChange={(val) => handleSelectAll("notificationsSMS", val)}
                />
              </div>
            </React.Fragment>}
            {notifications && notifications.userNotificationsSettings.map(n => <React.Fragment key={n.id}>
              <div ref={notificationsMap[n.notificationsType].ref}>
                <p
                  className={`tw-paragraph-reg ${!notifications.notificationsOn ? "opacity-50" : ""}`}
                >
                  {notificationsMap[n.notificationsType]?.label || ""}
                </p>
              </div>
              <button
                id={`US-${n.notificationsType}-Email`}
                data-cy={`US-${n.notificationsType}-Email`}
                disabled={!notifications.notificationsOn}
                type="button"
                onClick={() => handleToggleButton(n.id, "notificationsEmails")}
                className={
                  classNames(
                    baseStyles,
                    "rounded-l-lg border-t border-r-0 border-b border-l border-gray-300",
                    `${!notifications.notificationsOn || n.notificationsType === "targettedNotifications" ? "opacity-50 cursor-not-allowed" : "hover:opacity-75"}`,
                    `${n.notificationsType === "targettedNotifications" ? "bg-[var(--color-gray-100)]" : ""}`,
                    `${notifications.notificationsOn && n.notificationsEmails ? "bg-[var(--color-green-light)]" : ""}`,
                  )
                }
              >
                {intl.formatMessage({ id: "form.notificationsEmails" })}
              </button>
              <button
                id={`US-${n.notificationsType}-Push`}
                data-cy={`US-${n.notificationsType}-Push`}
                disabled={!notifications.notificationsOn}
                type="button"
                onClick={() => handleToggleButton(n.id, "notificationsPush")}
                className={
                  classNames(
                    "border border-gray-300",
                    baseStyles,
                    `${!notifications.notificationsOn ? "opacity-50 cursor-not-allowed" : "hover:opacity-75"}`,
                    `${notifications.notificationsOn && n.notificationsPush ? "bg-[var(--color-green-light)]" :""}`,
                  )
                }
              >
                {intl.formatMessage({ id: "form.notificationsPush" })}
              </button>
              <button
                id={`US-${n.notificationsType}-SMS`}
                data-cy={`US-${n.notificationsType}-SMS`}
                disabled={!notifications.notificationsOn}
                type="button"
                onClick={() => handleToggleButton(n.id, "notificationsSMS")}
                className={
                  classNames(
                    baseStyles,
                    "rounded-r-lg border-t border-r border-b border-l-0 border-gray-300",
                    `${!notifications.notificationsOn || n.notificationsType === "targettedNotifications" ? "opacity-50 cursor-not-allowed" : "hover:opacity-75"}`,
                    `${n.notificationsType === "targettedNotifications" ? "bg-[var(--color-gray-100)]" : ""}`,
                    `${notifications.notificationsOn &&n.notificationsSMS ? "bg-[var(--color-green-light)]" : ""}`,
                  )
                }
              >
                {intl.formatMessage({ id: "form.notificationsSMS" })}
              </button>
            </React.Fragment>)}
          </div>
          <div>
            {notifications && Object.keys(notificationsMap).map((key) => (
              <Tooltip
                key={key}
                anchorRef={notificationsMap[key]?.ref}
                text={notificationsMap[key]?.tooltipText}
              />
            ))}
          </div>
        </div>
      }
    </div>
  )
};

export default UserSettingsNotificationsModal;